<template>
  <div>
    <waiting-indicator-icon class="d-flex justify-content-center" color="cyan"
                            :name="LOAD_REPORT_INDICATOR"></waiting-indicator-icon>
    <div class="row group-topic mb-5" v-for="(group, i) in groupTopics" v-if="group.topics.length > 0">
      <div class="group-title d-lg-flex justify-content-between mb-2">
        <div class="d-flex justify-content-center gap-2">
          <div class="bullet" :style="{ 'background-color': COLORS[i] }"></div>
          <h5 class="fw-bold">{{ group.text }}</h5>
        </div>
        <basic-button :text="trans('general.observations.add_note')"
                      :icon="statusReports[group.id].note ? 'fi fi-rr-edit' : 'fi fi-rr-comment'"
                      @click="addNote({...group,...statusReports[group.id]})" icon-position="right"
                      :cls="(statusReports[group.id].note ? 'btn-edit btn-secondary ' : 'btn-add btn-secondary ') +  ' btn-sm'"/>
      </div>
      <div>
        <div class="topic-item card bg-gray-100 mb-2" v-for="topic in group.topics">
          <div class="card-body d-lg-flex justify-content-between ">
            <div class="topic-text flex-grow-1 mw-50">
              <p class="fw-bold mb-0 text-dark">{{ topic.text }}</p>
              <small> {{ trans('general.observations.last_update') }}: <span v-if="!loadingValues"> {{ getTopicStatusDate(topic) }}</span></small>
            </div>

            <assessment-horizontal-select v-if="!loadingValues" :options="assessmentValues"
                                          @change="logObservation($event, topic)"
                                          v-model="statusReports[topic.id]"
                                          class="flex-grow-1 mw-50"/>
            <div class="flex-grow-1 mw-5" v-else>
              <p class="placeholder-glow">
                <span class="placeholder col-12"></span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

      <div v-if="!loadingValues && groupTopics.length === 0">
          <no-content-placeholder
              v-if="hasAnyRole(['super_admin', 'administrator'])"
              icon="fi-rr-puzzle"
              :title="trans('general.development_report.no_dev_form_found')"
              :text="trans('general.development_report.no_dev_form_found_text_admin')"
              action="development-forms"
              :actionText="trans('general.development_report.create_development_form_action')"
              :help-slug="'topic/create-observation-forms'"/>

          <no-content-placeholder
              v-if="hasAnyRole(['teacher'])"
              icon="fi-rr-puzzle"
              :title="trans('general.development_report.no_dev_form_found')"
              :text="trans('general.development_report.no_dev_form_found_text_teacher')"
              :help-slug="'topic/observe-development-report'"/>
      </div>
  </div>
</template>

<script>
import axios from "axios";
import WaitingIndicatorIcon from "../../../theme/atoms/WaitingIndicatorIcon.vue";
import AssessmentHorizontalSelect from "../../../theme/atoms/AssessmentHorizontalSelect.vue";
import BasicButton from "../../../theme/atoms/BasicButton.vue";
import moment from "moment";
import {REPORT_TYPE} from "../../../services/Defaults";
import {COLORS} from "../services/StatusDataToChartData";
import noContentPlaceholder from "../../../theme/organisms/NoContentPlaceholder.vue";

export const LOAD_REPORT_INDICATOR = 'LOAD_REPORT_INDICATOR';

export default {
  name: "ObservationForm",
  props: {
    classId: {
      required: true,
      type: Number | String
    },
    childId: {
      required: false,
      type: Number | String
    },
    notifyParent: {
      required: false,
      type: Boolean
    },
    statusData: {
      required: false,
      type: Object
    },
    roles: {
        required: true,
        type: Array
    }
  },
  components: {
    AssessmentHorizontalSelect,
    WaitingIndicatorIcon,
    BasicButton,
      noContentPlaceholder
  },
  data() {
    return {
      COLORS,
      showOnDaily: this.notifyParent ?? true,
      groupTopics: [],
      assessmentValues: [],
      statusReports: {},
      LOAD_REPORT_INDICATOR: LOAD_REPORT_INDICATOR,
      loadingValues: true
    }
  },
  watch: {
    childId: function () {
      this.reload();
    },
  },
  methods: {
      hasAnyRole(roles) {
          const lowerCaseRolesToCheck = roles.map(role => role.toLowerCase());
          return this.roles?.some(userRole => lowerCaseRolesToCheck.includes(userRole.name.toLowerCase()));
      },
    addNote(groupTopic) {
      this.$emit('addNote', {
        ...groupTopic,
        development_topic_id: groupTopic.development_topic_id ?? groupTopic.id,
        topic:  groupTopic.topic ?? groupTopic.text,
        childId: this.childId
      });
    },
    loadAssessmentValues() {
      if (this.statusData.grade) {
        this.assessmentValues = this.statusData.grade;
        return;
      }

      axios.get(`/settings/assessment`, {params: {limit: 100}})
          .then((result) => {
            this.assessmentValues = result.data.data.data;
          });
    },
    loadReportForm() {
      if (this.statusData.topics) {
        this.groupTopics = this.statusData.topics;
        this.loadChildReportStatus(true);
        return;
      }

      const params = {include: 'groupTopics.topics', order_by: 'created_at', sorted_by: 'DESC'};
      this.$wait.start(LOAD_REPORT_INDICATOR);
      axios.get(`/development-reports/class/${this.classId}/active-reports`, {params})
          .then((response) => {
            const data = response.data.data;
            this.groupTopics = data[0].group_topics
            this.loadChildReportStatus();
            this.$wait.end(LOAD_REPORT_INDICATOR);
          }).catch(() => this.$wait.end(LOAD_REPORT_INDICATOR));
    },
    loadChildReportStatus(force = true) {
      if (!force && this.statusData.observations){
        this.initStatusReports();
        for (const status of this.statusData.observations) {
          this.statusReports[status.development_topic_id] = status;
        }
        this.loadingValues = false;
        return;
      }

      this.loadingValues = true;
      this.initStatusReports();

      axios.get(`/development-reports/${this.childId}/status`)
          .then((response) => {
            this.loadingValues = false;
            for (const status of response.data.data) {
              this.statusReports[status.development_topic_id] = status;
            }
          }).catch(() => this.$wait.end(LOAD_REPORT_INDICATOR));
    },
    reload() {
      this.groupTopics = [];
      this.loadReportForm();
    },
    initStatusReports() {
      this.statusReports = {};
      for (const group of this.groupTopics) {
        this.statusReports[group.id] = {assessment_value: null, note: null};

        for (const topic of group.topics) {
          this.statusReports[topic.id] = {assessment_value: null, note: null};
        }
      }
    },
    getTopicStatusDate(topic) {
      return this.statusReports[topic.id]?.created_at ?
          moment(this.statusReports[topic.id]?.created_at).format('DD MMMM, YYYY') : '-'
    },
    logObservation(assessment, topic) {
      const form = this.getReportForm(assessment, topic);
      axios.post('/child-report/new', form, {headers: {'content-type': 'multipart/form-data'}})
          .then(response => {
            this.$emit('observationAdded', response.data.data);
            this.$notify({
              title: trans('general.defaults.success'),
              text: '✅ ' + trans('general.observations.updated'),
              type: 'success'
            });
          })
    },
    getReportForm(assessment, topic) {
      const form = new FormData();

      form.append('children_ids[]', this.childId);
      form.append('model', REPORT_TYPE.DEVELOPMENT);
      form.append('show_on_daily', false)
      form.append(`assessments[0][topic]`, topic.text)
      form.append(`assessments[0][development_topic_id]`, topic.id)
      form.append(`assessments[0][assessment_value]`, assessment.assessment_value)
      form.append(`assessments[0][assessment]`, assessment.label)
      form.append(`assessments[0][assessment_icon]`, assessment.description)
      form.append(`assessments[0][assessment_id]`, assessment.id)

      return form;
    }
  },
  mounted() {
    this.loadAssessmentValues();
    this.reload();
  }
}
</script>

<style scoped>
.bullet {
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  border-radius: 50%;
  display: inline-block;
}
@media (min-width: 992px) {
  .mw-50 {
    max-width: 50%;
  }
}
</style>
